import AuthDeleteUserResponse from "./AuthResponses/AuthDeleteUserResponse";
import AuthMeResponse from "./AuthResponses/AuthMeResponse";
import AuthLoginResponse from "./AuthResponses/AuthLoginResponse";
import AuthSignupResponse from "./AuthResponses/AuthSignupResponse";
import AuthUserExistsResponse from "./AuthResponses/AuthUserExistsResponse";
import RepositoryBase from "./RepositoryBase";
import { ResponseEnvelope } from "./RepositoryBase";
import AuthChangePasswordResponse from "./AuthResponses/AuthChangePasswordResponse";

export default class LoginRepository extends RepositoryBase {

  // ---------- POST /auth/login ----------
  public static async login(username: string, password: string, force?: boolean, retried?: boolean): Promise<ResponseEnvelope<AuthLoginResponse>> {
    try {
      const csrf = await this.getCsrf(force || retried);
      if (csrf.isFailed()) return new ResponseEnvelope<AuthLoginResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/login`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
        body: JSON.stringify({ username: username, password: password })
      });

      // A stale antiforgery token yields 400; retry once with a freshly minted one.
      if (response.status === 400 && !retried) return this.login(username, password, force, true);

      if (!response.ok) return this.invalidResponse<AuthLoginResponse>();

      const raw = await response.json();
      if (!raw.success) {
        // Surface what the server actually said (e.g. a lockout) instead of
        // collapsing every rejection into a generic "invalid credentials".
        return new ResponseEnvelope<AuthLoginResponse>(
          false,
          undefined,
          "Login failed",
          raw.errorMessage ?? "Invalid username or password"
        );
      }

      const data: AuthLoginResponse = {
        success: true,
        errorMessage: raw.errorMessage ?? null,
        username: raw.username,
        fullName: raw.fullName,
        userId: raw.userId,
        locationId: raw.locationId,
        permissions: raw.permissions
      } as AuthLoginResponse;

      // Refresh CSRF after login so subsequent authenticated writes do not
      // reuse a token that was issued before the auth state changed. The
      // session cookie is already set at this point, so a failure here must
      // NOT be reported as a failed login — drop the cached token instead and
      // let the next call mint a new one.
      const refreshedCsrf = await this.getCsrf(true);
      if (refreshedCsrf.isFailed()) this.clearCsrf();

      return new ResponseEnvelope<AuthLoginResponse>(true, data);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }

  // ---------- GET /auth/me ----------
  public static async me(): Promise<ResponseEnvelope<AuthMeResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed()) return new ResponseEnvelope<AuthMeResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/me`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' }
      });

      if (!response.ok) return this.invalidResponse<AuthMeResponse>();

      const raw = await response.json();

      const data: AuthMeResponse = {
        username: raw.username,
        permissions: raw.permissions,
        fullName: raw.fullName,
        userId: raw.userId,
        locationId: raw.locationId
      } as AuthMeResponse;

      return new ResponseEnvelope<AuthMeResponse>(true, data);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }

  // ---------- POST /auth/logout ----------
  public static async logout(): Promise<ResponseEnvelope<null>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed()) return new ResponseEnvelope(false, null, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/logout`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'X-CSRF-Token': token }
      });

      if (!response.ok) return this.invalidResponse<null>();
      this.clearCsrf();
      return new ResponseEnvelope<null>(true, null);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }

  // ---------- GET /auth/exist/{workerId} ----------
  public static async userExists(workerId: string): Promise<ResponseEnvelope<AuthUserExistsResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed()) return new ResponseEnvelope<AuthUserExistsResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/exist/${encodeURIComponent(workerId)}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' }
      });

      if (!response.ok) return this.invalidResponse<AuthUserExistsResponse>();

      const raw = await response.json();

      const data: AuthUserExistsResponse = {
        exists: Boolean(raw.exists)
      } as AuthUserExistsResponse;

      return new ResponseEnvelope<AuthUserExistsResponse>(true, data);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }

  // ---------- POST /auth/user ----------
  public static async signup(
    username: string,
    password: string,
    workerId: string,
    email: string,
    locationId: string
  ): Promise<ResponseEnvelope<AuthSignupResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed()) return new ResponseEnvelope<AuthSignupResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/user`, {
        method: 'POST',
        mode: "cors",
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          username: username,
          password: password,
          workerId: workerId,
          email: email,
          locationId: locationId
        })
      });

      if (!response.ok) return this.invalidResponse<AuthSignupResponse>();

      const raw = await response.json();
      if (!raw.success) return this.requestUnsuccessful<AuthSignupResponse>();

      const data: AuthSignupResponse = {
        success: true,
        errorMessage: raw.errorMessage ?? null,
        userId: raw.userId,
      } as AuthSignupResponse;

      return new ResponseEnvelope<AuthSignupResponse>(true, data);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }

  // ---------- PUT /auth/user/{userId}/password ----------
  public static async changePassword(
    userId: string,
    oldPassword: string,
    newPassword: string
  ): Promise<ResponseEnvelope<AuthChangePasswordResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed()) return new ResponseEnvelope<AuthChangePasswordResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/user/${encodeURIComponent(userId)}/password`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          oldPassword: oldPassword,
          newPassword: newPassword
        })
      });

      if (!response.ok) return this.invalidResponse<AuthChangePasswordResponse>();

      const raw = await response.json();
      if (!raw.success) return this.requestUnsuccessful<AuthChangePasswordResponse>();

      const data: AuthChangePasswordResponse = {
        success: true,
        errorMessage: raw.errorMessage ?? null
      } as AuthChangePasswordResponse;

      return new ResponseEnvelope<AuthChangePasswordResponse>(true, data);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }

  // ---------- DELETE /auth/{userId} ----------
  public static async deleteUser(userId: string): Promise<ResponseEnvelope<AuthDeleteUserResponse>> {
    try {
      const csrf = await this.getCsrf();
      if (csrf.isFailed()) return new ResponseEnvelope<AuthDeleteUserResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
      const token = csrf.result!;

      const response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/auth/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: { 'X-CSRF-Token': token, 'Accept': 'application/json' }
      });

      if (!response.ok) return this.invalidResponse<AuthDeleteUserResponse>();

      const raw = await response.json();

      const data: AuthDeleteUserResponse = {
        success: Boolean(raw.success)
      } as AuthDeleteUserResponse;

      if (data.success !== true) return this.requestUnsuccessful<AuthDeleteUserResponse>();
      return new ResponseEnvelope<AuthDeleteUserResponse>(true, data);
    } catch (err) {
      if (err instanceof SyntaxError) return this.invalidFormat();
      if (err instanceof TypeError)   return this.unableToSendRequest();
      console.log(err);               return this.unexpectedError();
    }
  }
}
